import gsap from 'gsap';
import { createEffectLayer } from './effects/canvas.js';
import { hideDuration, hideText, showText, stopText } from './text-swap.js';

// ─── Config ───────────────────────────────────────────────────────────────────

const selectors = {
  root: '[data-magic-ball]',
  canvas: '[data-magic-ball-canvas]',
  action: '[data-magic-ball-action]',
  ask: '[data-magic-ball-action="ask"]',
  phrase: '[data-magic-ball-phrase]',
  question: '[data-magic-ball-question]',
  reveal: '[data-magic-ball-reveal]',
};

const defaultStep = 2.9;

/**
 * The result opens from the centre out: the question dissolves in first, then
 * after `delay` the title and the actions (`data-magic-ball-reveal`) fade in.
 */

const reveal = { delay: 0.15, duration: 0.8, ease: 'circ.out' };
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const instances = new WeakMap();

// ─── Data ─────────────────────────────────────────────────────────────────────

function parseList(json, name) {
  const list = JSON.parse(json ?? '[]');

  if (!Array.isArray(list) || !list.length) {
    throw new Error(`magic-ball: data-magic-ball-${name} must be a non-empty JSON array`);
  }

  return list;
}

function shuffle(list) {
  const result = [...list];

  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }

  return result;
}

/**
 * Shuffle bag: every question comes up once before the pool refills, and the
 * first draw of a new pool never repeats the last draw of the previous one.
 */

function createQuestionBag(questions) {
  let pool = [];
  let last = null;

  function refill() {
    pool = shuffle(questions);

    if (pool.length > 1 && pool.at(-1) === last) {
      [pool[0], pool[pool.length - 1]] = [pool[pool.length - 1], pool[0]];
    }
  }

  return {
    next() {
      if (!pool.length) refill();
      last = pool.pop();
      return last;
    },
  };
}

/**
 * The single place the question comes from. Async on purpose: swapping the
 * static bag for an API call must not touch the controller.
 */

async function getQuestion(bag) {
  return bag.next();
}

// ─── Controller ───────────────────────────────────────────────────────────────

function createMagicBall(root) {
  const controller = new AbortController();
  const { signal } = controller;

  const phraseElement = root.querySelector(selectors.phrase);
  const questionElement = root.querySelector(selectors.question);
  const askElement = root.querySelector(selectors.ask);
  const revealElements = root.querySelectorAll(selectors.reveal);

  const phrases = parseList(root.dataset.magicBallPhrases, 'phrases');
  const bag = createQuestionBag(parseList(root.dataset.magicBallQuestions, 'questions'));
  const step = Number(root.dataset.magicBallStep) || defaultStep;

  const layer = createEffectLayer({ root, canvas: root.querySelector(selectors.canvas) });

  let timeline = null;
  let run = 0;
  let question = '';
  let revealTween = null;

  /** Returns the effect's promise for its loading run, or null (see the effect layer). */

  function setState(state) {
    root.dataset.state = state;

    if (state !== 'idle') layer.setHover(false);
    if (state !== 'result') layer.setTint(false);

    return layer.setState(state);
  }

  /**
   * Resolves when the phrases have played or the timeline was interrupted.
   * Phrase `i` swaps in at exactly `i × step`, so the whole run lasts
   * `list.length × step`, close to the ring's loading run.
   */

  function playPhrases(list) {
    timeline?.kill();
    showText(phraseElement, list[0]);

    return new Promise((resolve) => {
      timeline = gsap.timeline({ onComplete: resolve, onInterrupt: resolve });

      list.slice(1).forEach((text, index) => {
        const swap = (index + 1) * step;

        timeline
          .call(hideText, [phraseElement], swap - hideDuration)
          .call(showText, [phraseElement, text], swap);
      });

      timeline.set({}, {}, list.length * step);
    });
  }

  /** The button that had focus is about to hide — park focus on the block. */

  function holdFocus() {
    const { activeElement } = document;

    if (activeElement === root || !root.contains(activeElement)) return;

    root.focus({ preventScroll: true });
  }

  function stopReveal() {
    revealTween?.kill();
    gsap.set(revealElements, { clearProps: 'opacity' });
  }

  function revealResult() {
    stopReveal();

    if (reducedMotion.matches) return;

    revealTween = gsap.fromTo(
      revealElements,
      { opacity: 0 },
      {
        opacity: 1,
        delay: reveal.delay,
        duration: reveal.duration,
        ease: reveal.ease,
        clearProps: 'opacity',
      }
    );
  }

  function showResult(text) {
    question = text;
    revealResult();
    showText(questionElement, text);
    setState('result');

    if (document.activeElement === root) askElement.focus({ preventScroll: true });
  }

  /** The result waits for the effect's loading run; only an effect without one falls back to the phrases. */

  async function divine(list) {
    const current = ++run;

    holdFocus();

    const finished = setState('loading');
    const phrased = playPhrases(list);
    const [next] = await Promise.all([getQuestion(bag), finished ?? phrased]);

    if (current !== run || signal.aborted) return;

    timeline?.kill();
    showResult(next);
  }

  function guide() {
    divine(reducedMotion.matches ? phrases.slice(0, 1) : phrases);
  }

  function ask() {
    root.dispatchEvent(new CustomEvent('magic-ball:ask', { bubbles: true, detail: { question } }));
  }

  const actions = { guide, retry: guide, ask };

  /** At rest the whole ball is the Guide me button, for the mouse; the real button stays for the keyboard. */

  function handleClick(event) {
    const { state } = root.dataset;
    const trigger = event.target.closest(selectors.action);

    if (state === 'loading') return;

    if (trigger) {
      actions[trigger.dataset.magicBallAction]?.();
      return;
    }

    if (state === 'idle') guide();
  }

  /** The resting ring draws in under a mouse; touch has no hover to show. */

  function handlePointer(event) {
    if (event.pointerType !== 'mouse') return;

    layer.setHover(event.type === 'pointerenter' && root.dataset.state === 'idle');
  }

  /** Hovering Ask tints the ring towards the accent. */

  function handleAskPointer(event) {
    if (event.pointerType !== 'mouse') return;

    layer.setTint(event.type === 'pointerenter');
  }

  const states = {
    idle: () => setState('idle'),
    loading: guide,
    result: async () => {
      const current = run;
      const next = await getQuestion(bag);

      if (current === run && !signal.aborted) showResult(next);
    },
  };

  /** Jump to a state from outside (the sandbox panel), cancelling any run in flight. */

  function show(state) {
    if (!states[state]) throw new Error(`magic-ball: unknown state "${state}"`);

    run++;
    timeline?.kill();
    stopText(phraseElement);
    stopReveal();
    states[state]();
  }

  function destroy() {
    run++;
    controller.abort();
    timeline?.kill();
    stopReveal();
    stopText(phraseElement);
    stopText(questionElement);
    layer.destroy();
    instances.delete(root);
  }

  root.addEventListener('click', handleClick, { signal });
  root.addEventListener('pointerenter', handlePointer, { signal });
  root.addEventListener('pointerleave', handlePointer, { signal });
  askElement.addEventListener('pointerenter', handleAskPointer, { signal });
  askElement.addEventListener('pointerleave', handleAskPointer, { signal });

  return { show, destroy };
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Idempotent: a second call on the same element returns the existing instance. */

export function initMagicBall(root) {
  if (instances.has(root)) return instances.get(root);

  const instance = createMagicBall(root);

  instances.set(root, instance);

  return instance;
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll(selectors.root).forEach(initMagicBall);
});
